import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useEffect, useState } from "react";
import { Ban, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import type { OfferedServiceStatus } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/lib/confirm";
import { SeletorDeEspecialidades } from "./SeletorDeEspecialidades";
import { parsePriceInput } from "./service-meta";
import { ehDonoOuSocio } from "@bookdental/schemas";

/** `inicial` na criação: o plano de tratamento abre o drawer já com o nome digitado e a especialidade do modo. */
export type DrawerMode =
  | { kind: "closed" }
  | { kind: "create"; inicial?: { nome?: string; especialidadeCodigo?: string } }
  | { kind: "edit"; id: string };

type Props = {
  mode: DrawerMode;
  onClose: () => void;
  /** Na criação, recebe o id do procedimento criado. */
  onSaved: (criadoId?: string) => void;
};

type FormState = {
  name: string;
  especialidadeIds: string[];
  /** A principal (agrupa nas telas); sempre uma das marcadas. */
  especialidadeId: string;
  priceInput: string;
  durationMinutes: string;
  color: string;
  cobradoPorDente: boolean;
  unidade: string;
  description: string;
  status: OfferedServiceStatus;
};

/** Cor default de um serviço novo — o mesmo cinza neutro que os chips usam pra quem não definiu. */
const DEFAULT_COLOR = "#A1A1AA";

const EMPTY: FormState = {
  name: "",
  especialidadeIds: [],
  especialidadeId: "",
  priceInput: "",
  durationMinutes: "",
  color: DEFAULT_COLOR,
  cobradoPorDente: false,
  unidade: "",
  description: "",
  status: "ACTIVE",
};

export function ServiceDrawer({ mode, onClose, onSaved }: Props) {
  const open = mode.kind !== "closed";
  const [values, setValues] = useState<FormState>(EMPTY);
  const { user } = useAuth();
  const isOwner = !!user?.activeOrganization?.permissions?.["prices.manage"];
  const ehDono = ehDonoOuSocio(user?.activeOrganization?.role);
  // Nome/especialidade/duração/cor são de quem gerencia configurações (era assim na aba Procedimentos
  // antes da fusão); preço continua trancado no dono.
  const canManage = ehDono || !!user?.activeOrganization?.permissions?.["settings.manage"];
  const confirm = useConfirm();
  // As especialidades da clínica aparecem à vista (marcadas como destaque); as outras ficam em "+ N outras".
  const todas = trpc.specialty.list.useQuery(undefined, { enabled: open }).data ?? [];
  const ativas = new Set((trpc.organization.activeSpecialties.useQuery(undefined, { enabled: open }).data ?? []).map((e) => e.id));
  const principais = todas.filter((e) => (ativas.size ? ativas.has(e.id) : e.destaque));
  const outras = todas.filter((e) => !principais.includes(e));

  const utils = trpc.useUtils();

  const detail = trpc.service.getById.useQuery(
    { id: mode.kind === "edit" ? mode.id : "" },
    { enabled: mode.kind === "edit" },
  );

  useEffect(() => {
    if (mode.kind === "create") {
      const especialidade = mode.inicial?.especialidadeCodigo ? todas.find((e) => e.code === mode.inicial!.especialidadeCodigo) : undefined;
      setValues({
        ...EMPTY,
        name: mode.inicial?.nome ?? "",
        especialidadeIds: especialidade ? [especialidade.id] : [],
        especialidadeId: especialidade?.id ?? "",
      });
    } else if (mode.kind === "edit" && detail.data) {
      setValues({
        name: detail.data.name ?? "",
        especialidadeIds: detail.data.especialidadeIds ?? [],
        especialidadeId: detail.data.especialidadeId ?? "",
        priceInput: ((detail.data.priceCents ?? 0) / 100).toFixed(2).replace(".", ","),
        durationMinutes: detail.data.durationMinutes ? String(detail.data.durationMinutes) : "",
        color: (detail.data as any).color ?? DEFAULT_COLOR,
        cobradoPorDente: !!(detail.data as any).cobradoPorDente,
        unidade: (detail.data as any).unidade ?? "",
        description: detail.data.description ?? "",
        status: detail.data.status ?? "ACTIVE",
      });
    }
    // `todas` entra pra a especialidade inicial aparecer quando a lista termina de carregar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, detail.data, todas.length]);

  const create = trpc.service.create.useMutation({
    onSuccess: (criado) => {
      toast.success("Procedimento criado.");
      utils.service.list.invalidate();
      utils.plano.procedimentos.invalidate();
      onSaved(criado?.id);
    },
    onError: (err) => toast.error(err.message || "Erro ao criar."),
  });
  const update = trpc.service.update.useMutation({
    onSuccess: () => {
      toast.success("Procedimento atualizado.");
      utils.service.list.invalidate();
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Erro ao atualizar."),
  });
  const disable = trpc.service.disable.useMutation({
    onSuccess: () => {
      toast.success("Procedimento desativado.");
      utils.service.list.invalidate();
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Erro ao desativar."),
  });
  const activate = trpc.service.activate.useMutation({
    onSuccess: () => {
      toast.success("Procedimento reativado.");
      utils.service.list.invalidate();
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Erro ao reativar."),
  });
  const remove = trpc.service.delete.useMutation({
    onSuccess: () => {
      toast.success("Procedimento excluído.");
      utils.service.list.invalidate();
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Erro ao excluir."),
  });

  const isSaving = create.isPending || update.isPending;

  async function handleDisable() {
    if (mode.kind !== "edit") return;
    const ok = await confirm({
      title: "Desativar procedimento?",
      description: "O procedimento some da lista de preços ativa, mas continua no histórico e pode ser reativado depois.",
      confirmText: "Desativar",
      cancelText: "Voltar",
      destructive: true,
    });
    if (ok) disable.mutate({ id: mode.id });
  }

  async function handleDelete() {
    if (mode.kind !== "edit") return;
    const ok = await confirm({
      title: "Excluir procedimento?",
      description:
        "Essa ação apaga o procedimento permanentemente. Agendamentos passados que usaram ele mantêm o histórico, mas perdem o vínculo com o catálogo. Pra manter o procedimento fora da lista sem apagar, use \"Desativar\" em vez disso.",
      confirmText: "Excluir",
      cancelText: "Voltar",
      destructive: true,
    });
    if (ok) remove.mutate({ id: mode.id });
  }

  const handleSave = () => {
    if (!values.name.trim()) {
      toast.error("Nome é obrigatório.");
      return;
    }
    if (mode.kind === "create" && values.especialidadeIds.length === 0) {
      toast.error("Escolha ao menos uma especialidade.");
      return;
    }
    const payload = {
      name: values.name.trim(),
      especialidadeIds: values.especialidadeIds,
      especialidadeId: values.especialidadeId || undefined,
      // Sem permissão de preço, nem manda o campo — o backend rejeita explicitamente quem envia
      // sem poder, então omitir é o que deixa o Admin salvar o resto sem esbarrar na regra.
      ...(isOwner ? { priceCents: parsePriceInput(values.priceInput) } : {}),
      durationMinutes: values.durationMinutes ? Number(values.durationMinutes) : undefined,
      color: values.color,
      cobradoPorDente: values.cobradoPorDente,
      unidade: values.unidade.trim() || null,
      description: values.description.trim() || undefined,
      status: values.status,
    };
    if (mode.kind === "create") create.mutate(payload);
    else if (mode.kind === "edit") update.mutate({ id: mode.id, ...payload });
  };

  if (!open) return null;

  // Defesa em profundidade — os pontos de entrada já ficam escondidos pra quem não gerencia, mas
  // se esse drawer abrir mesmo assim, não mostra formulário nenhum.
  if (!canManage) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-sm border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          <DialogHeader>
            <DialogTitle className="text-base">Sem permissão</DialogTitle>
            <DialogDescription>Seu papel não pode gerenciar os procedimentos.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        closeLabel={mode.kind === "create" ? "Fechar novo procedimento" : "Fechar edição do procedimento"}
        className="flex max-h-[85dvh] max-w-lg flex-col gap-0 overflow-hidden border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        <DialogHeader className="space-y-0 border-b border-zinc-100 px-5 py-4 pr-12 text-left dark:border-border">
          <DialogDescription className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
            {mode.kind === "create" ? "Novo procedimento" : "Editar procedimento"}
          </DialogDescription>
          <DialogTitle className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground" ajuda={AJUDA_CATALOGO.procedimento}>
            {values.name || "Procedimento"}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {mode.kind === "edit" && detail.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : (
            <div className="space-y-3">
              <Section title="Identificação">
                <Field label="Nome do procedimento *">
                  <Input
                    value={values.name}
                    onChange={(v) => setValues({ ...values, name: v })}
                    autoFocus={mode.kind === "create"}
                  />
                </Field>
              </Section>

              <Section title="Especialidades *">
                <p className="-mt-1 mb-2 text-[11.5px] text-zinc-500 dark:text-muted-foreground">
                  Em que especialidades o procedimento serve — ele aparece no cartão de cada uma em Configurações.
                </p>
                <SeletorDeEspecialidades
                  principais={principais}
                  outras={outras}
                  marcadas={values.especialidadeIds}
                  onChange={(especialidadeIds) =>
                    // Tirou a principal (ou marcou a primeira): a principal passa a ser a primeira marcada.
                    setValues({
                      ...values,
                      especialidadeIds,
                      especialidadeId: especialidadeIds.includes(values.especialidadeId) ? values.especialidadeId : (especialidadeIds[0] ?? ""),
                    })
                  }
                />
                {values.especialidadeIds.length > 1 && (
                  <Field label="Especialidade principal">
                    <select
                      value={values.especialidadeId || values.especialidadeIds[0]}
                      onChange={(e) => setValues({ ...values, especialidadeId: e.target.value })}
                      className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
                    >
                      {todas
                        .filter((e) => values.especialidadeIds.includes(e.id))
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.label}
                          </option>
                        ))}
                    </select>
                  </Field>
                )}
              </Section>

              <Section title={isOwner ? "Preço e duração" : "Duração"}>
                <div className="grid grid-cols-2 gap-2">
                  {isOwner && (
                    <Field label="Preço (R$) *">
                      <Input
                        value={values.priceInput}
                        onChange={(v) => setValues({ ...values, priceInput: v })}
                        inputMode="decimal"
                        placeholder="0,00"
                      />
                    </Field>
                  )}
                  <Field label="Duração (min)">
                    <Input
                      value={values.durationMinutes}
                      onChange={(v) => setValues({ ...values, durationMinutes: v.replace(/\D/g, "") })}
                      inputMode="numeric"
                      placeholder="Opcional"
                    />
                  </Field>
                </div>
                <label className="mt-2 flex items-center gap-2 text-[12.5px] text-zinc-700 dark:text-foreground">
                  <input
                    type="checkbox"
                    checked={values.cobradoPorDente}
                    onChange={(e) => setValues({ ...values, cobradoPorDente: e.target.checked })}
                    className="h-4 w-4 rounded border-zinc-300"
                  />
                  Cobrado por dente (no plano de tratamento, o preço multiplica pelos dentes)
                </label>
                <div className="mt-2">
                  <Field label="Unidade da quantidade">
                    <Input
                      value={values.unidade}
                      onChange={(v) => setValues({ ...values, unidade: v.slice(0, 20) })}
                      placeholder="Harmonização: U, ml, frasco"
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Cor na agenda">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={values.color}
                    onChange={(e) => setValues({ ...values, color: e.target.value })}
                    aria-label="Cor na agenda"
                    className="h-9 w-12 cursor-pointer rounded-md border border-zinc-200 bg-white dark:border-border"
                  />
                  <p className="text-[11.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
                    Cor do card quando este procedimento é escolhido numa consulta. Consultas já
                    agendadas mantêm a cor que tinham.
                  </p>
                </div>
              </Section>

              <Section title="Descrição">
                <Field label="Descrição">
                  <textarea
                    value={values.description}
                    onChange={(e) => setValues({ ...values, description: e.target.value })}
                    rows={3}
                    className="w-full resize-none bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
                    placeholder="Detalhes adicionais sobre o procedimento..."
                  />
                </Field>
              </Section>
            </div>
          )}
        </div>

        <footer
          className="flex items-center gap-2 border-t border-zinc-100 px-5 py-3 dark:border-border"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          {mode.kind === "edit" && values.status === "INACTIVE" && (
            <button
              type="button"
              onClick={() => {
                if (mode.kind !== "edit") return;
                activate.mutate({ id: mode.id });
              }}
              disabled={activate.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 px-2.5 text-[12px] font-medium text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-60 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
            >
              {activate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              Reativar
            </button>
          )}
          {mode.kind === "edit" && values.status === "ACTIVE" && (
            <button
              type="button"
              onClick={handleDisable}
              disabled={disable.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-muted-foreground dark:hover:bg-white/5"
            >
              {disable.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
              Desativar
            </button>
          )}
          {mode.kind === "edit" && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={remove.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-2.5 text-[12px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              {remove.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Excluir
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {mode.kind === "create" ? "Criar procedimento" : "Salvar"}
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "block rounded-lg border border-zinc-200 px-3 py-2 transition-colors focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 dark:border-border",
        className,
      )}
    >
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
  inputMode?: "decimal" | "numeric" | "text";
}) {
  return (
    <input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
    />
  );
}
